import { useTranslation } from 'react-i18next';
import type { UseTranslationResponse } from 'react-i18next';

import { getStrictTranslation } from '@vlabs/monokit/shared/lib/utils';

export const keyPrefix = 'facestreamAnalytic';
export const useFacestreamAnalyticTranslation = (): UseTranslationResponse<'entities', typeof keyPrefix> => useTranslation('entities', { keyPrefix });

export const getFacestreamAnalyticStrictTranslation = (key: string): string | undefined => (
  getStrictTranslation(`${keyPrefix}.${key}`, 'entities')
);

const ru = {
  sections: {
    basic: {
      title: 'Основные параметры видеоаналитики',
      mode: {
        label: 'Режим',
        tooltip: 'Определяет что аналитика должна искать в кадре.',
      },
      handler: {
        label: 'ID обработчика для лучших кадров',
        placeholder: 'ID обработчика для лучших кадров',
        tooltip: 'Уникальный handler_id для обработки биометрических образцов лиц или тел по заданным правилам. При использовании обработчика генерируется событие и обрабатывается согласно настройкам сценария.',
      },
      frame_processing_mode: {
        label: 'Режим обработки кадров',
        tooltip: 'Определяет способ обработки кадров: Автоматический — режим выбирается автоматически между «Полным кадром» и «Масштабируемым кадром». Полный кадр — кадр конвертируется в RGB нужного размера сразу после декодирования; качество выше, скорость обработки ниже. Масштабируемый кадр — размер изображения адаптируется в зависимости от настроек TrackEngine.',
      },
      ffmpeg_threads_number: {
        label: 'Количество потоков для декодирования видео',
        tooltip: 'Задает количество потоков для декодирования видео с помощью FFMPEG. Чем больше потоков, тем больше ядер процессора задействуется. Рекомендуется увеличивать для видео высокого разрешения (4K и выше).',
      },
      real_time_mode_fps: {
        label: 'Максимальный FPS для обработки видео',
        tooltip: 'Ограничивает частоту кадров при обработке видеофайлов. Видео не будет обрабатываться быстрее указанного FPS. Полезно для тестирования производительности.',
      },
      preferred_width: {
        label: 'Предпочитаемая ширина видеопотока',
        tooltip: 'Используется для потоков с несколькими вариантами качества (разное разрешение и битрейт) и позволяет выбрать канал с шириной, наиболее близкой к указанной.',
      },
    },
    roi_droi: {
      title: 'ROI/DROI',
      roi: {
        label: 'Координаты ROI',
        tooltip: 'Зона интереса: координаты в формате x,y,width,height. Редактирование текстом — запасной вариант, когда ретрансляция недоступна.',
      },
      droi: {
        label: 'Координаты DROI',
        tooltip: 'Зона, исключаемая из анализа: координаты в формате x,y,width,height.',
      },
      pixels: 'Пиксели',
      percent: 'Проценты',
    },
    sending: {
      title: 'Параметры отправки изображений',
      bestshot_type: {
        label: 'Тип отправки изображений лучших кадров лиц и тел',
        tooltip: 'Какие снимки будут отправляться в рамках трека или промежутка, для последующей обработки в системе.',
      },
      face_bestshots: {
        label: 'Количество кадров лица в рамках трека',
        tooltip: 'Сколько лучших снимков будет отправлено из трека или временного промежутка. Увеличение значения повышает вероятность правильного распознавания лица, но увеличивает нагрузку на систему.',
      },
      body_bestshots: {
        label: 'Количество кадров тела в рамках трека',
        tooltip: 'Сколько лучших снимков будет отправлено из трека или временного промежутка. Увеличение значения повышает вероятность правильного распознавания тела, но увеличивает нагрузку на систему.',
      },
      send_only_full_set: {
        label: 'Отправлять только полный набор кадров тела',
        tooltip: 'Позволяет посылать данные (лучшие снимки и детекции) только если имеется необходимое количество лучших снимков ("Количество кадров, которое пользователь хочет получить из трека или какого-то промежутка времени на этом треке") и длина трека ("Минимальный размер детекции для главного трека").',
      },
      delete_track_after_sending: {
        label: 'Удалять данные о лучших снимках и детекциях после отправки',
        tooltip: 'Включите, чтобы после отправки данные удалялись из памяти.',
      },
      full_frame_settings: {
        label: 'Тип отправки изображений полного кадра события',
        tooltip: 'Определяет какой кадр будет сохранен в качестве кадра события: "Лицо" - Кадр с лучшим снимком лица; "Тело" - Кадр с лучшим снимком тела; "Лицо+Тело" - Кадр, на котором будет и лицо и тело; "Отдельно Лица и Тела" - будут сохранены 2 кадра - отдельно с лучшим снимком лица и отдельно с лучшим снимком тела.',
      },
      sending_type: {
        label: 'Тип измерения периода анализа кадров и периода ожидания',
        tooltip: 'Указывает, измерять период в секундах или по количеству кадров.',
      },
      time_period_of_searching: {
        label: 'Период анализа кадров',
        tooltip: 'Если значение равно "-1", анализ ведется по всем кадрам, до конца трека',
      },
      silent_period: {
        label: 'Период ожидания',
        tooltip: 'Если значение равно "-1", система удерживает период ожидания на неопределенный срок',
      },
    },
    filtering: {
      title: 'Параметры фильтрации',
      min_score_face: {
        label: 'Порог фильтрации детекций лиц',
        tooltip: 'Минимально качество детекции в событии. Рекомендуемое значение для лиц - 0.5187',
      },
      min_score_body: {
        label: 'Порог фильтрации детекций тел',
        tooltip: 'Минимально качество детекции в событии. Рекомендуемое значения для тел - 0.5',
      },
      detection_yaw_threshold: {
        label: 'Пороговый угол yaw',
        tooltip: 'Максимально допустимый угол поворота головы. Кадры с большим углом не учитываются.',
      },
      detection_pitch_threshold: {
        label: 'Пороговый угол pitch',
        tooltip: 'Максимально допустимый угол наклона головы вверх/вниз. Кадры с большим углом не учитываются.',
      },
      detection_roll_threshold: {
        label: 'Пороговый угол roll',
        tooltip: 'Максимально допустимый угол наклона головы влево/вправо. Кадры с большим углом не учитываются.',
      },
      yaw_collection_mode: {
        label: 'Анализировать несколько кадров по углу поворота головы',
        tooltip: 'Позволяет точнее определить угол поворота головы, однако увеличивает нагрузку на систему.',
      },
      yaw_number: {
        label: 'Количество кадров для фильтрации по углу',
      },
      mouth_occlusion_threshold: {
        label: 'Порог перекрытия рта',
        tooltip: 'Максимальная допустимая степень перекрытия рта на кадре.',
      },
      min_body_size_threshold: {
        label: 'Минимальный размер тела для детекции',
        tooltip: 'Минимальный размер детекции тела для отправки на обработку.',
      },
    },
    liveness: {
      title: 'Параметры liveness',
      liveness_weight_1: { label: 'Вес liveness (1)' },
      liveness_weight_2: { label: 'Вес liveness (2)' },
      mask_liveness: { label: 'Фильтрация по маскам' },
      flying_liveness: { label: 'Фильтрация «летающих» лиц' },
      liveness_mode: { label: 'Режим liveness' },
      number_of_liveness_checks: { label: 'Количество проверок liveness' },
      liveness_threshold: { label: 'Порог liveness' },
      mask_backgrounds_count: { label: 'Количество фонов масок' },
    },
    healthcheck: {
      title: 'Проверка состояния потока',
      healthcheck_period: { label: 'Период проверки состояния потока' },
    },
    primary_track: {
      title: 'Primary track',
      use_primary_track: { label: 'Использовать primary track' },
      best_shot_min_size: { label: 'Минимальный размер лучшего кадра' },
      best_shot_proper_size: { label: 'Оптимальный размер лучшего кадра' },
    },
  },
  options: {
    mode_1: 'Лица',
    mode_2: 'Тела',
    mode_3: 'Лица и тела',
    frame_auto: 'Автоматический',
    frame_full: 'Полный кадр',
    frame_scale: 'Масштабируемый кадр',
    sending_sec: 'По времени (сек)',
    sending_frames: 'По кадрам',
    bestshot_1: 'Лицо',
    bestshot_2: 'Тело',
    bestshot_3: 'Лицо + тело',
    bestshot_4: 'Не отправлять',
    liveness_0: '0',
    liveness_1: '1',
    liveness_2: '2',
  },
  validation: {
    required: 'Обязательное поле',
    uuid: 'Некорректный UUID',
  },
  delete_confirm: {
    title: 'Удаление видеоаналитики',
    subtitle: 'Видеоаналитика будет удалена из группы безвозвратно',
    buttons: {
      cancel: 'Отмена',
      delete: 'Удалить',
    },
  },
  delete_analytics: 'Удалить видеоаналитику',
  on_delete_success: 'Видеоаналитика удалена',
};

export const ruRu = (__LOCALE_RU__ ? ({
  [keyPrefix]: ru,
}) : {}) as Record<string, unknown>;

export const en = (__LOCALE_EN__ ? ({
  [keyPrefix]: {
    sections: {
      basic: {
        title: 'Basic video analytics parameters',
        mode: {
          label: 'Mode',
          tooltip: 'Defines what the analytics should look for in the frame.',
        },
        handler: {
          label: 'Best shot handler ID',
          placeholder: 'Best shot handler ID',
          tooltip: 'Unique handler_id for processing biometric samples of faces or bodies according to the specified rules. When the handler is used, an event is generated and processed according to the scenario settings.',
        },
        frame_processing_mode: {
          label: 'Frame processing mode',
          tooltip: 'Defines how frames are processed: Automatic — the mode is selected automatically between "Full frame" and "Scaled frame". Full frame — the frame is converted to RGB of the required size right after decoding; higher quality, lower processing speed. Scaled frame — the image size adapts depending on the TrackEngine settings.',
        },
        ffmpeg_threads_number: {
          label: 'Video decoding threads',
          tooltip: 'Sets the number of threads for video decoding with FFMPEG. The more threads, the more CPU cores are used. It is recommended to increase for high-resolution video (4K and above).',
        },
        real_time_mode_fps: {
          label: 'Max FPS for video processing',
          tooltip: 'Limits the frame rate when processing video files. Video will not be processed faster than the specified FPS. Useful for performance testing.',
        },
        preferred_width: {
          label: 'Preferred video stream width',
          tooltip: 'Used for streams with multiple quality options (different resolution and bitrate) and allows selecting the channel with the width closest to the specified one.',
        },
      },
      roi_droi: {
        title: 'ROI/DROI',
        roi: {
          label: 'ROI coordinates',
          tooltip: 'Region of interest: coordinates as x,y,width,height. Text editing is a fallback when retransmission is unavailable.',
        },
        droi: {
          label: 'DROI coordinates',
          tooltip: 'Region excluded from analysis: coordinates as x,y,width,height.',
        },
        pixels: 'Pixels',
        percent: 'Percent',
      },
      sending: {
        title: 'Image sending parameters',
        bestshot_type: {
          label: 'Face/body best shot sending type',
          tooltip: 'Which snapshots will be sent within a track or a time period for further processing in the system.',
        },
        face_bestshots: {
          label: 'Face frames per track',
          tooltip: 'How many best snapshots will be sent from a track or time period. Increasing the value improves the probability of correct face recognition but increases the system load.',
        },
        body_bestshots: {
          label: 'Body frames per track',
          tooltip: 'How many best snapshots will be sent from a track or time period. Increasing the value improves the probability of correct body recognition but increases the system load.',
        },
        send_only_full_set: {
          label: 'Send only full body frame set',
          tooltip: 'Allows sending data (best snapshots and detections) only if the required number of best snapshots ("Number of frames the user wants to receive from a track or a certain time period on this track") and track length ("Minimum detection size for the main track") are available.',
        },
        delete_track_after_sending: {
          label: 'Delete best shots and detections after sending',
          tooltip: 'Enable so that the data is deleted from memory after sending.',
        },
        full_frame_settings: {
          label: 'Event full frame sending type',
          tooltip: 'Defines which frame will be saved as the event frame: "Face" - Frame with the best face snapshot; "Body" - Frame with the best body snapshot; "Face+Body" - Frame with both face and body; "Face and Body separately" - 2 frames will be saved - separately with the best face snapshot and separately with the best body snapshot.',
        },
        sending_type: {
          label: 'Measurement type of analysis and waiting periods',
          tooltip: 'Specifies whether the period is measured in seconds or by the number of frames.',
        },
        time_period_of_searching: {
          label: 'Frame analysis period',
          tooltip: 'If the value is "-1", the analysis runs over all frames until the end of the track',
        },
        silent_period: {
          label: 'Waiting period',
          tooltip: 'If the value is "-1", the system holds the waiting period indefinitely',
        },
      },
      filtering: {
        title: 'Filtering parameters',
        min_score_face: {
          label: 'Face detection filter threshold',
          tooltip: 'Minimum detection quality in an event. Recommended value for faces is 0.5187',
        },
        min_score_body: {
          label: 'Body detection filter threshold',
          tooltip: 'Minimum detection quality in an event. Recommended value for bodies is 0.5',
        },
        detection_yaw_threshold: {
          label: 'Yaw threshold angle',
          tooltip: 'Maximum allowed head rotation angle. Frames with a larger angle are ignored.',
        },
        detection_pitch_threshold: {
          label: 'Pitch threshold angle',
          tooltip: 'Maximum allowed head tilt up/down angle. Frames with a larger angle are ignored.',
        },
        detection_roll_threshold: {
          label: 'Roll threshold angle',
          tooltip: 'Maximum allowed head tilt left/right angle. Frames with a larger angle are ignored.',
        },
        yaw_collection_mode: {
          label: 'Analyze multiple frames by head rotation angle',
          tooltip: 'Allows determining the head rotation angle more accurately, however it increases the system load.',
        },
        yaw_number: {
          label: 'Frames for angle filtering',
        },
        mouth_occlusion_threshold: {
          label: 'Mouth occlusion threshold',
          tooltip: 'Maximum allowed mouth occlusion degree in a frame.',
        },
        min_body_size_threshold: {
          label: 'Minimum body size for detection',
          tooltip: 'Minimum body detection size to be sent for processing.',
        },
      },
      liveness: {
        title: 'Liveness parameters',
        liveness_weight_1: { label: 'Liveness weight (1)' },
        liveness_weight_2: { label: 'Liveness weight (2)' },
        mask_liveness: { label: 'Mask filtering' },
        flying_liveness: { label: 'Flying faces filtering' },
        liveness_mode: { label: 'Liveness mode' },
        number_of_liveness_checks: { label: 'Number of liveness checks' },
        liveness_threshold: { label: 'Liveness threshold' },
        mask_backgrounds_count: { label: 'Mask backgrounds count' },
      },
      healthcheck: {
        title: 'Stream health check',
        healthcheck_period: { label: 'Stream health check period' },
      },
      primary_track: {
        title: 'Primary track',
        use_primary_track: { label: 'Use primary track' },
        best_shot_min_size: { label: 'Minimum best shot size' },
        best_shot_proper_size: { label: 'Optimal best shot size' },
      },
    },
    options: {
      mode_1: 'Faces',
      mode_2: 'Bodies',
      mode_3: 'Faces and bodies',
      frame_auto: 'Automatic',
      frame_full: 'Full frame',
      frame_scale: 'Scaled frame',
      sending_sec: 'By time (sec)',
      sending_frames: 'By frames',
      bestshot_1: 'Face',
      bestshot_2: 'Body',
      bestshot_3: 'Face + body',
      bestshot_4: 'Do not send',
      liveness_0: '0',
      liveness_1: '1',
      liveness_2: '2',
    },
    validation: {
      required: 'Required field',
      uuid: 'Invalid UUID',
    },
    delete_confirm: {
      title: 'Delete video analytics',
      subtitle: 'Video analytics will be permanently removed from the group',
      buttons: {
        cancel: 'Cancel',
        delete: 'Delete',
      },
    },
    delete_analytics: 'Delete video analytics',
    on_delete_success: 'Video analytics deleted',
  },
}) : {}) as Record<string, unknown>;

export const esEs = (__LOCALE_ESES__ ? ({
  [keyPrefix]: {
    sections: {
      basic: {
        title: 'Parámetros básicos de videoanalítica',
        mode: {
          label: 'Modo',
          tooltip: 'Define qué debe buscar la analítica en el fotograma.',
        },
        handler: {
          label: 'ID del controlador de mejores capturas',
          placeholder: 'ID del controlador de mejores capturas',
          tooltip: 'handler_id único para procesar muestras biométricas de caras o cuerpos según las reglas especificadas. Al usar el controlador se genera un evento y se procesa según la configuración del escenario.',
        },
        frame_processing_mode: {
          label: 'Modo de procesamiento de fotogramas',
          tooltip: 'Define cómo se procesan los fotogramas: Automático — el modo se selecciona automáticamente entre «Fotograma completo» y «Fotograma escalado». Fotograma completo — el fotograma se convierte a RGB del tamaño necesario inmediatamente después de la decodificación; mayor calidad, menor velocidad de procesamiento. Fotograma escalado — el tamaño de la imagen se adapta según la configuración de TrackEngine.',
        },
        ffmpeg_threads_number: {
          label: 'Hilos de decodificación de video',
          tooltip: 'Establece el número de hilos para decodificar video con FFMPEG. Cuantos más hilos, más núcleos de CPU se utilizan. Se recomienda aumentar para video de alta resolución (4K y superior).',
        },
        real_time_mode_fps: {
          label: 'FPS máximo para procesamiento',
          tooltip: 'Limita la frecuencia de fotogramas al procesar archivos de video. El video no se procesará más rápido que el FPS especificado. Útil para pruebas de rendimiento.',
        },
        preferred_width: {
          label: 'Ancho preferido del flujo de video',
          tooltip: 'Se usa para flujos con múltiples variantes de calidad (diferente resolución y bitrate) y permite seleccionar el canal con el ancho más cercano al especificado.',
        },
      },
      roi_droi: {
        title: 'ROI/DROI',
        roi: {
          label: 'Coordenadas de ROI',
          tooltip: 'Región de interés: coordenadas como x,y,width,height. La edición de texto es una alternativa cuando la retransmisión no está disponible.',
        },
        droi: {
          label: 'Coordenadas de DROI',
          tooltip: 'Región excluida del análisis: coordenadas como x,y,width,height.',
        },
        pixels: 'Píxeles',
        percent: 'Porcentaje',
      },
      sending: {
        title: 'Parámetros de envío de imágenes',
        bestshot_type: {
          label: 'Tipo de envío de mejores capturas de cara/cuerpo',
          tooltip: 'Qué capturas se enviarán dentro de un track o período, para su posterior procesamiento en el sistema.',
        },
        face_bestshots: {
          label: 'Fotogramas de cara por track',
          tooltip: 'Cuántas mejores capturas se enviarán de un track o período de tiempo. Aumentar el valor mejora la probabilidad de reconocimiento correcto de la cara, pero aumenta la carga del sistema.',
        },
        body_bestshots: {
          label: 'Fotogramas de cuerpo por track',
          tooltip: 'Cuántas mejores capturas se enviarán de un track o período de tiempo. Aumentar el valor mejora la probabilidad de reconocimiento correcto del cuerpo, pero aumenta la carga del sistema.',
        },
        send_only_full_set: {
          label: 'Enviar solo el conjunto completo de fotogramas de cuerpo',
          tooltip: 'Permite enviar datos (mejores capturas y detecciones) solo si se dispone del número necesario de mejores capturas ("Número de fotogramas que el usuario quiere recibir de un track o de un período de tiempo en ese track") y la longitud del track ("Tamaño mínimo de detección para el track principal").',
        },
        delete_track_after_sending: {
          label: 'Eliminar mejores capturas y detecciones tras el envío',
          tooltip: 'Actívelo para que los datos se eliminen de la memoria después del envío.',
        },
        full_frame_settings: {
          label: 'Tipo de envío del fotograma completo del evento',
          tooltip: 'Define qué fotograma se guardará como fotograma del evento: "Cara" - Fotograma con la mejor captura de cara; "Cuerpo" - Fotograma con la mejor captura de cuerpo; "Cara+Cuerpo" - Fotograma con cara y cuerpo; "Cara y Cuerpo por separado" - se guardarán 2 fotogramas - por separado con la mejor captura de cara y por separado con la mejor captura de cuerpo.',
        },
        sending_type: {
          label: 'Tipo de medición de periodos de análisis y espera',
          tooltip: 'Indica si el periodo se mide en segundos o por número de fotogramas.',
        },
        time_period_of_searching: {
          label: 'Periodo de análisis de fotogramas',
          tooltip: 'Si el valor es "-1", el análisis se realiza sobre todos los fotogramas hasta el final del track',
        },
        silent_period: {
          label: 'Periodo de espera',
          tooltip: 'Si el valor es "-1", el sistema mantiene el periodo de espera indefinidamente',
        },
      },
      filtering: {
        title: 'Parámetros de filtrado',
        min_score_face: {
          label: 'Umbral de filtrado de detecciones de cara',
          tooltip: 'Calidad mínima de detección en un evento. El valor recomendado para caras es 0.5187',
        },
        min_score_body: {
          label: 'Umbral de filtrado de detecciones de cuerpo',
          tooltip: 'Calidad mínima de detección en un evento. El valor recomendado para cuerpos es 0.5',
        },
        detection_yaw_threshold: {
          label: 'Ángulo umbral yaw',
          tooltip: 'Ángulo máximo permitido de rotación de la cabeza. Los fotogramas con un ángulo mayor no se tienen en cuenta.',
        },
        detection_pitch_threshold: {
          label: 'Ángulo umbral pitch',
          tooltip: 'Ángulo máximo permitido de inclinación de la cabeza arriba/abajo. Los fotogramas con un ángulo mayor no se tienen en cuenta.',
        },
        detection_roll_threshold: {
          label: 'Ángulo umbral roll',
          tooltip: 'Ángulo máximo permitido de inclinación de la cabeza izquierda/derecha. Los fotogramas con un ángulo mayor no se tienen en cuenta.',
        },
        yaw_collection_mode: {
          label: 'Analizar varios fotogramas por ángulo de rotación de cabeza',
          tooltip: 'Permite determinar con mayor precisión el ángulo de rotación de la cabeza, sin embargo aumenta la carga del sistema.',
        },
        yaw_number: {
          label: 'Fotogramas para filtrado por ángulo',
        },
        mouth_occlusion_threshold: {
          label: 'Umbral de oclusión de boca',
          tooltip: 'Grado máximo permitido de oclusión de la boca en un fotograma.',
        },
        min_body_size_threshold: {
          label: 'Tamaño mínimo del cuerpo para detección',
          tooltip: 'Tamaño mínimo de detección del cuerpo para enviar al procesamiento.',
        },
      },
      liveness: {
        title: 'Parámetros de liveness',
        liveness_weight_1: { label: 'Peso de liveness (1)' },
        liveness_weight_2: { label: 'Peso de liveness (2)' },
        mask_liveness: { label: 'Filtrado por máscaras' },
        flying_liveness: { label: 'Filtrado de caras voladoras' },
        liveness_mode: { label: 'Modo de liveness' },
        number_of_liveness_checks: { label: 'Número de comprobaciones de liveness' },
        liveness_threshold: { label: 'Umbral de liveness' },
        mask_backgrounds_count: { label: 'Número de fondos de máscaras' },
      },
      healthcheck: {
        title: 'Comprobación del estado del flujo',
        healthcheck_period: { label: 'Periodo de comprobación del estado del flujo' },
      },
      primary_track: {
        title: 'Primary track',
        use_primary_track: { label: 'Usar primary track' },
        best_shot_min_size: { label: 'Tamaño mínimo de la mejor captura' },
        best_shot_proper_size: { label: 'Tamaño óptimo de la mejor captura' },
      },
    },
    options: {
      mode_1: 'Caras',
      mode_2: 'Cuerpos',
      mode_3: 'Caras y cuerpos',
      frame_auto: 'Automático',
      frame_full: 'Fotograma completo',
      frame_scale: 'Fotograma escalado',
      sending_sec: 'Por tiempo (seg)',
      sending_frames: 'Por fotogramas',
      bestshot_1: 'Cara',
      bestshot_2: 'Cuerpo',
      bestshot_3: 'Cara + cuerpo',
      bestshot_4: 'No enviar',
      liveness_0: '0',
      liveness_1: '1',
      liveness_2: '2',
    },
    validation: {
      required: 'Campo obligatorio',
      uuid: 'UUID no válido',
    },
    delete_confirm: {
      title: 'Eliminar videoanalítica',
      subtitle: 'La videoanalítica se eliminará del grupo de forma permanente',
      buttons: {
        cancel: 'Cancelar',
        delete: 'Eliminar',
      },
    },
    delete_analytics: 'Eliminar videoanalítica',
    on_delete_success: 'Videoanalítica eliminada',
  },
}) : {}) as Record<string, unknown>;

export const ptBr = (__LOCALE_PTBR__ ? ({
  [keyPrefix]: {
    sections: {
      basic: {
        title: 'Parâmetros básicos da videoanalítica',
        mode: {
          label: 'Modo',
          tooltip: 'Define o que a analítica deve procurar no quadro.',
        },
        handler: {
          label: 'ID do manipulador de melhores capturas',
          placeholder: 'ID do manipulador de melhores capturas',
          tooltip: 'handler_id exclusivo para processar amostras biométricas de rostos ou corpos de acordo com as regras especificadas. Ao usar o manipulador, um evento é gerado e processado de acordo com as configurações do cenário.',
        },
        frame_processing_mode: {
          label: 'Modo de processamento de quadros',
          tooltip: 'Define como os quadros são processados: Automático — o modo é selecionado automaticamente entre «Quadro completo» e «Quadro escalado». Quadro completo — o quadro é convertido para RGB do tamanho necessário logo após a decodificação; qualidade superior, velocidade de processamento inferior. Quadro escalado — o tamanho da imagem se adapta conforme as configurações do TrackEngine.',
        },
        ffmpeg_threads_number: {
          label: 'Threads de decodificação de vídeo',
          tooltip: 'Define o número de threads para decodificar vídeo com FFMPEG. Quanto mais threads, mais núcleos de CPU são utilizados. Recomenda-se aumentar para vídeo de alta resolução (4K e superior).',
        },
        real_time_mode_fps: {
          label: 'FPS máximo para processamento',
          tooltip: 'Limita a taxa de quadros ao processar arquivos de vídeo. O vídeo não será processado mais rápido que o FPS especificado. Útil para testes de desempenho.',
        },
        preferred_width: {
          label: 'Largura preferida do fluxo de vídeo',
          tooltip: 'Usado para fluxos com múltiplas variantes de qualidade (resolução e bitrate diferentes) e permite selecionar o canal com a largura mais próxima da especificada.',
        },
      },
      roi_droi: {
        title: 'ROI/DROI',
        roi: {
          label: 'Coordenadas de ROI',
          tooltip: 'Região de interesse: coordenadas como x,y,width,height. A edição de texto é uma alternativa quando a retransmissão não está disponível.',
        },
        droi: {
          label: 'Coordenadas de DROI',
          tooltip: 'Região excluída da análise: coordenadas como x,y,width,height.',
        },
        pixels: 'Pixels',
        percent: 'Porcentagem',
      },
      sending: {
        title: 'Parâmetros de envio de imagens',
        bestshot_type: {
          label: 'Tipo de envio de melhores capturas de rosto/corpo',
          tooltip: 'Quais capturas serão enviadas dentro de um track ou período, para processamento posterior no sistema.',
        },
        face_bestshots: {
          label: 'Quadros de rosto por track',
          tooltip: 'Quantas melhores capturas serão enviadas de um track ou período de tempo. Aumentar o valor melhora a probabilidade de reconhecimento correto do rosto, mas aumenta a carga do sistema.',
        },
        body_bestshots: {
          label: 'Quadros de corpo por track',
          tooltip: 'Quantas melhores capturas serão enviadas de um track ou período de tempo. Aumentar o valor melhora a probabilidade de reconhecimento correto do corpo, mas aumenta a carga do sistema.',
        },
        send_only_full_set: {
          label: 'Enviar apenas o conjunto completo de quadros de corpo',
          tooltip: 'Permite enviar dados (melhores capturas e detecções) somente se houver o número necessário de melhores capturas ("Número de quadros que o usuário deseja receber de um track ou de um determinado período de tempo nesse track") e o comprimento do track ("Tamanho mínimo de detecção para o track principal").',
        },
        delete_track_after_sending: {
          label: 'Excluir melhores capturas e detecções após o envio',
          tooltip: 'Ative para que os dados sejam excluídos da memória após o envio.',
        },
        full_frame_settings: {
          label: 'Tipo de envio do quadro completo do evento',
          tooltip: 'Define qual quadro será salvo como quadro do evento: "Rosto" - Quadro com a melhor captura de rosto; "Corpo" - Quadro com a melhor captura de corpo; "Rosto+Corpo" - Quadro com rosto e corpo; "Rosto e Corpo separadamente" - 2 quadros serão salvos - separadamente com a melhor captura de rosto e separadamente com a melhor captura de corpo.',
        },
        sending_type: {
          label: 'Tipo de medição dos períodos de análise e espera',
          tooltip: 'Indica se o período é medido em segundos ou pelo número de quadros.',
        },
        time_period_of_searching: {
          label: 'Período de análise de quadros',
          tooltip: 'Se o valor for "-1", a análise é feita em todos os quadros até o final do track',
        },
        silent_period: {
          label: 'Período de espera',
          tooltip: 'Se o valor for "-1", o sistema mantém o período de espera indefinidamente',
        },
      },
      filtering: {
        title: 'Parâmetros de filtragem',
        min_score_face: {
          label: 'Limiar de filtragem de detecções de rosto',
          tooltip: 'Qualidade mínima de detecção em um evento. O valor recomendado para rostos é 0.5187',
        },
        min_score_body: {
          label: 'Limiar de filtragem de detecções de corpo',
          tooltip: 'Qualidade mínima de detecção em um evento. O valor recomendado para corpos é 0.5',
        },
        detection_yaw_threshold: {
          label: 'Ângulo limiar yaw',
          tooltip: 'Ângulo máximo permitido de rotação da cabeça. Quadros com ângulo maior são ignorados.',
        },
        detection_pitch_threshold: {
          label: 'Ângulo limiar pitch',
          tooltip: 'Ângulo máximo permitido de inclinação da cabeça para cima/baixo. Quadros com ângulo maior são ignorados.',
        },
        detection_roll_threshold: {
          label: 'Ângulo limiar roll',
          tooltip: 'Ângulo máximo permitido de inclinação da cabeça para esquerda/direita. Quadros com ângulo maior são ignorados.',
        },
        yaw_collection_mode: {
          label: 'Analisar vários quadros pelo ângulo de rotação da cabeça',
          tooltip: 'Permite determinar com mais precisão o ângulo de rotação da cabeça, porém aumenta a carga do sistema.',
        },
        yaw_number: {
          label: 'Quadros para filtragem por ângulo',
        },
        mouth_occlusion_threshold: {
          label: 'Limiar de oclusão da boca',
          tooltip: 'Grau máximo permitido de oclusão da boca em um quadro.',
        },
        min_body_size_threshold: {
          label: 'Tamanho mínimo do corpo para detecção',
          tooltip: 'Tamanho mínimo de detecção do corpo para envio ao processamento.',
        },
      },
      liveness: {
        title: 'Parâmetros de liveness',
        liveness_weight_1: { label: 'Peso de liveness (1)' },
        liveness_weight_2: { label: 'Peso de liveness (2)' },
        mask_liveness: { label: 'Filtragem por máscaras' },
        flying_liveness: { label: 'Filtragem de rostos voadores' },
        liveness_mode: { label: 'Modo de liveness' },
        number_of_liveness_checks: { label: 'Número de verificações de liveness' },
        liveness_threshold: { label: 'Limiar de liveness' },
        mask_backgrounds_count: { label: 'Número de fundos de máscaras' },
      },
      healthcheck: {
        title: 'Verificação do estado do fluxo',
        healthcheck_period: { label: 'Período de verificação do estado do fluxo' },
      },
      primary_track: {
        title: 'Primary track',
        use_primary_track: { label: 'Usar primary track' },
        best_shot_min_size: { label: 'Tamanho mínimo da melhor captura' },
        best_shot_proper_size: { label: 'Tamanho ideal da melhor captura' },
      },
    },
    options: {
      mode_1: 'Rostos',
      mode_2: 'Corpos',
      mode_3: 'Rostos e corpos',
      frame_auto: 'Automático',
      frame_full: 'Quadro completo',
      frame_scale: 'Quadro escalado',
      sending_sec: 'Por tempo (seg)',
      sending_frames: 'Por quadros',
      bestshot_1: 'Rosto',
      bestshot_2: 'Corpo',
      bestshot_3: 'Rosto + corpo',
      bestshot_4: 'Não enviar',
      liveness_0: '0',
      liveness_1: '1',
      liveness_2: '2',
    },
    validation: {
      required: 'Campo obrigatório',
      uuid: 'UUID inválido',
    },
    delete_confirm: {
      title: 'Excluir videoanalítica',
      subtitle: 'A videoanalítica será excluída permanentemente do grupo',
      buttons: {
        cancel: 'Cancelar',
        delete: 'Excluir',
      },
    },
    delete_analytics: 'Excluir videoanalítica',
    on_delete_success: 'Videoanalítica excluída',
  },
}) : {}) as Record<string, unknown>;
